import { useEffect, useState } from "react";
import { HotelNavigationUtility } from "@b2bportal/core-utilities";
import type { GuestsSelection, Suggestion } from "@b2bportal/lodging-api";
import dayjs from "dayjs";
import { useSessionContext } from "@/components/AuthModule/SessionContext";

const RECENT_SEARCHES_KEY = "perkspot-recent-searches";
const OLD_HOTEL_SEARCH_KEY = "perkspot-hotel-searches";
const DAYS_FORWARD = 14;
const MAX_SEARCHES = 4;

interface BaseRecentSearch {
  type: "hotel" | "car";
  customerId?: string;
  timestamp: number;
}

export interface HotelRecentSearch extends BaseRecentSearch {
  type: "hotel";
  fromDate: string;
  untilDate: string;
  label: string;
  subLabel: string;
  guests: GuestsSelection;
  roomsCount: number;
  place: Suggestion;
}

export interface CarRecentSearch extends BaseRecentSearch {
  type: "car";
  pickupDate: string;
  dropoffDate: string;
  pickupTime: string;
  dropoffTime: string;
  pickupLocation: {
    label: string;
    identifier: string;
  };
  dropoffLocation: {
    label: string;
    identifier: string;
  };
  driverAge: string;
}

export type RecentSearch = HotelRecentSearch | CarRecentSearch;

export const useRecentSearches = () => {
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const { sessionInfo } = useSessionContext();

  useEffect(() => {
    const recentSearches = retrieveRecentSearches(sessionInfo?.hopperUserId);
    setRecentSearches(recentSearches);
  }, [sessionInfo?.hopperUserId]);

  return {
    recentSearches,
  };
};

export const retrieveRecentSearches = (customerId?: string): RecentSearch[] => {
  try {
    const searchKey = customerId
      ? `${RECENT_SEARCHES_KEY}_${customerId}`
      : RECENT_SEARCHES_KEY;

    // Old-format entries have no place ID so they can't be replayed anymore;
    // just clean up the abandoned key.
    const oldSearchKey = customerId
      ? `${OLD_HOTEL_SEARCH_KEY}_${customerId}`
      : OLD_HOTEL_SEARCH_KEY;
    window.localStorage.removeItem(oldSearchKey);

    const recentSearches = window.localStorage.getItem(searchKey);

    if (!recentSearches) {
      return [];
    }

    const parsedRecentSearches = JSON.parse(recentSearches) as RecentSearch[];

    if (!Array.isArray(parsedRecentSearches)) {
      console.warn(
        "Invalid data format for recent searches:",
        parsedRecentSearches
      );
      return [];
    }

    const mappedSearches = parsedRecentSearches
      .filter(isCompleteSearch)
      .map(mapExpiredSearch)
      .slice(0, MAX_SEARCHES);
    saveRecentSearches(mappedSearches, customerId);
    return mappedSearches;
  } catch (error) {
    console.error(
      "Failed to retrieve recent searches from localStorage:",
      error
    );
    return [];
  }
};

export const saveRecentHotelSearch = (
  params: Omit<HotelRecentSearch, "type" | "timestamp">
) => {
  const search: HotelRecentSearch = {
    ...params,
    type: "hotel",
    timestamp: Date.now(),
  };
  saveRecentSearch(search);
};

export const saveRecentCarSearch = (
  params: Omit<CarRecentSearch, "type" | "timestamp">
) => {
  const search: CarRecentSearch = {
    ...params,
    type: "car",
    timestamp: Date.now(),
  };
  saveRecentSearch(search);
};

const saveRecentSearch = (params: RecentSearch) => {
  try {
    const recentSearches = retrieveRecentSearches(params.customerId);

    const isSaved = recentSearches.some((search) =>
      isSameSearch(search, params)
    );

    if (isSaved) {
      saveRecentSearches(
        [params, ...recentSearches.filter((p) => !isSameSearch(p, params))],
        params.customerId
      );
      return;
    }

    saveRecentSearches(
      [params, ...recentSearches.slice(0, MAX_SEARCHES - 1)],
      params.customerId
    );
  } catch (error) {
    console.error("Failed to save recent search:", error);
  }
};

const saveRecentSearches = (searches: RecentSearch[], customerId?: string) => {
  try {
    const searchKey = customerId
      ? `${RECENT_SEARCHES_KEY}_${customerId}`
      : RECENT_SEARCHES_KEY;
    window.localStorage.setItem(searchKey, JSON.stringify(searches));
  } catch (error) {
    console.error("Failed to save recent searches:", error);
  }
};

// Hotel entries persisted before `place` was stored can't build the commerce
// portal URL, so they are dropped (and purged by the re-save in retrieve).
const isCompleteSearch = (search: RecentSearch): boolean =>
  search.type === "car" ||
  (search as Partial<HotelRecentSearch>).place?.id?.Id === "Lodgings";

const mapExpiredSearch = (search: RecentSearch): RecentSearch => {
  const today = dayjs();

  if (search.type === "hotel") {
    const isPastToday = dayjs(search.fromDate).isBefore(today);

    if (!isPastToday) {
      return search;
    }

    const diff = dayjs(search.untilDate).diff(dayjs(search.fromDate), "days");

    return {
      ...search,
      fromDate: HotelNavigationUtility.formatDateForUrl(
        today.add(DAYS_FORWARD, "days")
      ),
      untilDate: HotelNavigationUtility.formatDateForUrl(
        today.add(DAYS_FORWARD + diff, "days")
      ),
    };
  } else {
    // Car search
    const isPastToday = dayjs(search.pickupDate).isBefore(today);

    if (!isPastToday) {
      return search;
    }

    const diff = dayjs(search.dropoffDate).diff(
      dayjs(search.pickupDate),
      "days"
    );

    return {
      ...search,
      pickupDate: HotelNavigationUtility.formatDateForUrl(
        today.add(DAYS_FORWARD, "days")
      ),
      dropoffDate: HotelNavigationUtility.formatDateForUrl(
        today.add(DAYS_FORWARD + diff, "days")
      ),
    };
  }
};

const isSameSearch = (obj1?: RecentSearch, obj2?: RecentSearch): boolean => {
  if (!obj1 || !obj2) {
    return false;
  }

  // Different types are never the same
  if (obj1.type !== obj2.type) {
    return false;
  }

  if (obj1.type === "hotel" && obj2.type === "hotel") {
    return obj1.label === obj2.label;
  } else if (obj1.type === "car" && obj2.type === "car") {
    return (
      obj1.pickupLocation.identifier === obj2.pickupLocation.identifier &&
      obj1.pickupDate === obj2.pickupDate
    );
  }

  return false;
};
